import React, {useState, useEffect, useCallback, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {KTCard, KTCardBody, Table, Loading} from '../../../components'
import {CustomCell} from '../../../components/Table/columns/CustomCell'
import {CustomCellDouble} from '../../../components/Table/columns/CustomCellDouble'
import {CustomHeader} from '../../../components/Table/columns/CustomHeader'
import {ListHeader} from '../../../components/Table/header/ListHeader'
import {ListLoading} from '../../../components/Table/ListLoading'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {proposalGetAll, proposalExcel} from '../../../redux/slices/proposal.slice'
import {projectLookup, basicLookup, allCompanyLookup} from '../../../redux/slices/lookup.slice'
import {useMemoizedStaticData} from '../../../utils/performanceOptimizations'

const selectProposalData = (state) => ({
  proposal: state.proposal.proposal,
  loading: state.proposal.loading
})

const selectLookupData = (state) => ({
  projectLookupList: state.lookup.projectLookupList,
  ProposalStatus: state.lookup.ProposalStatus,
  companyLookup: state.lookup.companyLookup
})

function List() {
  const intl = useIntl()
  const {proposal, loading} = useSelector(selectProposalData)
  const {projectLookupList, ProposalStatus, companyLookup} = useSelector(selectLookupData)
  const dispatch = useDispatch()

  const [params, setParams] = useState()

  useEffect(() => {
    if (params) dispatch(proposalGetAll(params))
  }, [params, dispatch])

  useEffect(() => {
    dispatch(projectLookup())
    dispatch(allCompanyLookup())
    dispatch(basicLookup('ProposalStatus'))
  }, [dispatch])

  const handleSorting = useCallback((item) => {
    setParams((prevState) => ({...prevState, sort: item && item.sort, order: item && item.order}))
  }, [])

  const handleSearch = useCallback((item) => {
    setParams((prevState) => ({...prevState, name: item}))
  }, [])

  const handlePagination = useCallback((pagination) => {
    setParams((prevState) => ({...prevState, ...pagination}))
  }, [])

  const handleExcelExport = useCallback(() => {
    dispatch(
      proposalExcel({
        params,
        fileName: intl.formatMessage({id: 'BC.PROPOSAL'}),
      })
    )
  }, [dispatch, params, intl])

  const handleFilterSubmit = useCallback((values) => {
    setParams({...values})
  }, [])

  const columns = useMemo(() => [
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'PROPOSAL.SEARCH.NAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'name',
      Cell: ({...props}) => <CustomCellDouble item={props} double='companyName' />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'PROPOSAL.SEARCH.PROJECTNAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'projectName',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'PROPOSAL.SEARCH.EDITDATE'})}
          className='min-w-125px'
        />
      ),
      accessor: 'editDate',
      Cell: ({...props}) => <CustomCell item={props} date='short' />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'PROPOSAL.SEARCH.DEFERMENTDATE'})}
          className='min-w-125px'
        />
      ),
      accessor: 'defermentDate',
      Cell: ({...props}) => <CustomCellDouble item={props} date='short' double='delayDay' />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'PROPOSAL.SEARCH.TOTALPRICE'})}
          className='min-w-125px'
        />
      ),
      accessor: 'totalPrice',
      Cell: ({...props}) => <CustomCell item={props} price />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'PROPOSAL.SEARCH.PROPOSALSTATUSNAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'proposalStatusName',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'LIST.LASTMODIFIEDINFO'})}
          className='min-w-125px'
        />
      ),
      accessor: 'lastModifiedBy',
      Cell: ({...props}) => <CustomCellDouble item={props} double='lastModifiedDate' />,
    },
  ], [intl, handleSorting])

  const actionButtons = useMemoizedStaticData(() => [
    {
      title: 'BUTTON.EXCEL',
      onClick: handleExcelExport,
      icon: '/images/icons/duotune/arrows/arr044.svg',
      variant: 'light-primary',
    },
    {
      title: 'FILTER',
      icon: '/images/icons/duotune/general/gen031.svg',
      variant: 'light-primary',
      list: [
        {
          title: 'PROPOSAL.SEARCH.PROJECTNAME',
          data: projectLookupList,
          name: 'projectId',
        },
        {
          title: 'PROPOSAL.CREATE.COMPANYID',
          data: companyLookup,
          name: 'companyId',
        },
        {
          title: 'PROPOSAL.SEARCH.PROPOSALSTATUSNAME',
          data: ProposalStatus,
          name: 'proposalStatusId',
        },
      ],
    },
  ], [projectLookupList, companyLookup, ProposalStatus, handleExcelExport])

  return (
    <>
      <PageTitle>{intl.formatMessage({id: 'BC.PROPOSAL'})}</PageTitle>
      <KTCard>
        <ListHeader
          search={handleSearch}
          actionButtons={actionButtons}
          onSubmit={handleFilterSubmit}
          pages={'PROPOSAL'}
        />
        <KTCardBody>
          {!proposal && <ListLoading />}
          {proposal && (
            <Table
              items={proposal}
              cols={columns}
              handlePagination={handlePagination}
              paddless
            />
          )}
        </KTCardBody>
      </KTCard>

      {loading && <Loading />}
    </>
  )
}
export {List}
